import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, ArrowUp, FileText, Plus } from 'lucide-react'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import { ProjectIcon } from '@/lib/utils/projectIcon'

interface DocSubpagesFooterProps {
  page: PageDocument
  pages: Array<PageDocument>
  canAddSubPage: boolean
  onAddSubPage: () => void
  onOpenPage: (slug: string) => void
}

const bySortOrder = (a: PageDocument, b: PageDocument) =>
  (a.sortOrder ?? '').localeCompare(b.sortOrder ?? '') || a.createdOn - b.createdOn

/**
 * Every page shows where it lives (design 4a rule 2): its subpages at the end,
 * a link up to the parent and on to the next sibling.
 */
export function DocSubpagesFooter({ page, pages, canAddSubPage, onAddSubPage, onOpenPage }: DocSubpagesFooterProps) {
  const { t } = useTranslation('projects')

  const { children, parent, next } = useMemo(() => {
    const live = pages.filter((p) => !p.isDeleted)
    // Top-level docs of other projects and spaces aren't siblings.
    const siblings = live
      .filter((p) => p.projectId === page.projectId && (p.parentPageId ?? null) === (page.parentPageId ?? null))
      .sort(bySortOrder)
    const index = siblings.findIndex((p) => p.id === page.id)
    return {
      children: live.filter((p) => p.parentPageId === page.id).sort(bySortOrder),
      parent: page.parentPageId ? live.find((p) => p.id === page.parentPageId) : undefined,
      next: index >= 0 ? siblings[index + 1] : undefined,
    }
  }, [pages, page.id, page.parentPageId, page.projectId])

  return (
    <section className="mt-10 border-t pt-6">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {t('docs.subpages')}
        </h2>
        {canAddSubPage && (
          <button
            type="button"
            onClick={onAddSubPage}
            className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <Plus className="size-3" />
            {t('docs.addSubpage')}
          </button>
        )}
      </div>
      {children.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">{t('docs.noSubpages')}</p>
      ) : (
        <ul className="grid gap-1.5 sm:grid-cols-2">
          {children.map((child) => (
            <li key={child.id}>
              <button
                type="button"
                onClick={() => onOpenPage(child.slug)}
                className="flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-[13.5px] hover:bg-secondary/40"
              >
                {child.icon ? (
                  <ProjectIcon iconType={child.iconType} icon={child.icon} iconColor={child.iconColor} size={14} />
                ) : (
                  <FileText className="size-3.5 text-muted-foreground" />
                )}
                <span className="truncate">{child.title || t('docs.untitled')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {(parent || next) && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-[13px]">
          {parent ? (
            <button
              type="button"
              onClick={() => onOpenPage(parent.slug)}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <ArrowUp className="size-3.5" />
              <span className="text-xs">{t('docs.parent')}</span>
              <span className="font-medium text-foreground">{parent.title || t('docs.untitled')}</span>
            </button>
          ) : (
            <span />
          )}
          {next && (
            <button
              type="button"
              onClick={() => onOpenPage(next.slug)}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <span className="text-xs">{parent ? t('docs.nextIn', { parent: parent.title }) : t('docs.next')}</span>
              <span className="font-medium text-foreground">{next.title || t('docs.untitled')}</span>
              <ArrowRight className="size-3.5" />
            </button>
          )}
        </div>
      )}
    </section>
  )
}
